import { useState, useEffect } from 'react';
import { db } from '../../../../firebase';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { RECAP_TIERS } from '../recap/recapTiers';
import { groupPreviousRecaps } from '../recap/previousRecapsModel';

/**
 * Previous Lesson Recaps for the student on this lesson: the read behind the
 * "Previous recaps" panel in the one page-owned RecapDrawer (2026-09-21).
 *
 * OWNED BY THE PAGE, not the drawer: it runs once per studentId and survives
 * every tab switch and every open/close of the drawer. The drawer only renders
 * what it is handed.
 *
 * ONE QUERY, ALREADY INDEXED: documents where studentId == X, visibility in
 * all three tiers, orderBy createdAt desc. That is the family read's shape
 * (LessonRecapService) widened to the teacher tier, and the deployed composite
 * index (studentId ASC, visibility ASC, createdAt DESC) serves it. The staff
 * read rule has no author or tier predicate, so every teacher's every tier
 * comes back: which is the feature: the rotation means the last four lessons
 * were usually taught by someone else.
 *
 * A one-shot getDocs rather than a listener: the panel is history, and a
 * listener on this query would re-fire on every autosave of TODAY's tiers
 * (they match the same query) for no visible change. The `ignore` flag is the
 * unsubscribe: a result landing after unmount is dropped.
 *
 * LIMIT is docs, not lessons: three tiers per lesson, so 30 covers ten lessons
 * before grouping drops blanks and today's, comfortably more than the four
 * shown.
 */
const ALL_VISIBILITIES = RECAP_TIERS.map((t) => t.visibility);
const DOC_LIMIT = 30;

const usePreviousRecaps = ({ studentId, currentLessonId, max = 4 }) => {
	const [lessons, setLessons] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');

	useEffect(() => {
		if (!studentId) {
			setLessons([]);
			return undefined;
		}
		let ignore = false;
		setIsLoading(true);
		setError('');
		const q = query(
			collection(db, 'documents'),
			where('studentId', '==', studentId),
			where('visibility', 'in', ALL_VISIBILITIES),
			orderBy('createdAt', 'desc'),
			limit(DOC_LIMIT)
		);
		getDocs(q)
			.then((snap) => {
				if (ignore) return;
				const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
				setLessons(groupPreviousRecaps(docs, { excludeLessonId: currentLessonId, max }));
			})
			.catch((err) => {
				if (ignore) return;
				console.error('Error loading previous recaps:', err);
				setError('Could not load earlier recaps.');
			})
			.finally(() => {
				if (!ignore) setIsLoading(false);
			});
		return () => {
			ignore = true;
		};
	}, [studentId, currentLessonId, max]);

	return { lessons, isLoading, error };
};

export default usePreviousRecaps;
